import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import classNames from 'classnames/bind';
import FocusedImage from 'Components/FocusedImage';
import AssetModel from 'Stores/AssetModel';
import CallToAction from 'Components/CallToAction';
import { tabTypes } from 'Utils/getContactTab';
import ConsumptionData from 'Components/ConsumptionData';
import Typography, { headlinePropTypes } from 'Components/Typography';
import RichText from 'Components/RichText';

const cx = classNames.bind(styles);

const ChessboardItem = ({
  bodyText,
  children,
  className,
  consumptionText,
  contentAlignment = 'left',
  contentPosition = 'left',
  cta1,
  cta2,
  deeplinkId,
  headline,
  image,
  onOpenContact,
}) => {
  const renderCta = (cta, buttonVariant) => {
    return (
      <div className={styles.cta}>
        <CallToAction {...cta} buttonVariant={buttonVariant} onOpenContact={onOpenContact} />
      </div>
    );
  };

  const itemContent = children || <RichText content={bodyText} />;
  const imageItemClassName = cx(styles.imageItem, {
    'position-left': contentPosition === 'left',
    'position-right': contentPosition === 'right',
  });

  if (!image) {
    return null;
  }

  const imageAsset = image ? new AssetModel(image) : undefined;

  return (
    <Fragment>
      <div className={imageItemClassName} id={deeplinkId}>
        {imageAsset && (
          <FocusedImage
            image={imageAsset}
            className={styles.image}
            useThumb={imageAsset && imageAsset.shouldUseThumb()}
          />
        )}
        <div
          className={cx('content', className, {
            'align-left': contentAlignment === 'left',
            'align-center': contentAlignment === 'center',
            'align-right': contentAlignment === 'right',
          })}
        >
          {headline && (
            <Typography.Headline
              className={styles.title}
              levelVisually="h3"
              additionalAriaLabelText={consumptionText}
              {...headline}
            />
          )}
          {itemContent}
          {cta1 && cta1.label && renderCta(cta1, cta1.buttonVariant ?? 'primary')}
          {cta2 && cta2.label && renderCta(cta2, cta2.buttonVariant ?? 'secondary')}
        </div>
        {consumptionText && (
          <div className={styles.consumptionText}>
            <ConsumptionData
              consumptionText={[consumptionText]}
              colorType="light"
              ariaHidden={true}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

ChessboardItem.propTypes = {
  bodyText: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  consumptionText: PropTypes.string,
  content: PropTypes.string, // the (text) element of the item as html string (use either children OR content)
  contentPosition: PropTypes.oneOf(['left', 'right']), // default left
  contentAlignment: PropTypes.oneOf(['left', 'center', 'right']), // default left
  cta1: PropTypes.shape({
    additional: PropTypes.string,
    href: PropTypes.string,
    label: PropTypes.string.isRequired,
    mazdaForm: PropTypes.bool,
    model: PropTypes.string,
    type: PropTypes.oneOf(tabTypes),
    target: PropTypes.string,
  }),
  cta2: PropTypes.shape({
    additional: PropTypes.string,
    href: PropTypes.string,
    label: PropTypes.string.isRequired,
    mazdaForm: PropTypes.bool,
    model: PropTypes.string,
    type: PropTypes.oneOf(tabTypes),
    target: PropTypes.string,
  }),
  deeplinkId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flex: PropTypes.bool,
  headline: PropTypes.shape(headlinePropTypes),
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  isInViewport: PropTypes.bool,
  onOpenContact: PropTypes.func,
  stopWatcher: PropTypes.func,
};

export default ChessboardItem;
